/**
 * Image URL Rewriter
 *
 * Translates legacy image URLs (Firebase Storage, Appwrite Storage) into the
 * new Cloudflare R2 URLs served by the Image Worker at REACT_APP_IMAGE_URL.
 *
 * Handles three URL families:
 *
 *  1. https://api.villakosh.in/v1/storage/buckets/{bucket}/files/{fileId}/view
 *     → https://images.villakosh.in/files/{fileId}
 *
 *  2. https://*.cloud.appwrite.io/v1/storage/buckets/{bucket}/files/{fileId}/view
 *     → https://images.villakosh.in/files/{fileId}
 *
 *  3. https://firebasestorage.googleapis.com/...
 *     → looked up in image-url-mapping.json (Firebase → Appwrite-fileId map)
 *     → https://images.villakosh.in/files/{fileId}
 *
 * The Firebase → fileId map is loaded lazily from /image-url-mapping.json
 * (copied into public/ at build time).
 */
import { IMAGE_URL } from './config';

let firebaseToFileId = null;
let mapPromise = null;

/** Lazy-load and parse the Firebase→fileId mapping. */
function loadFirebaseMap() {
  if (firebaseToFileId !== null) return Promise.resolve(firebaseToFileId);
  if (mapPromise) return mapPromise;

  mapPromise = fetch('/image-url-mapping.json')
    .then((r) => (r.ok ? r.json() : {}))
    .then((rawMap) => {
      // Build firebaseUrl → fileId index by extracting fileId from each value
      const idx = Object.create(null);
      for (const [firebaseUrl, appwriteUrl] of Object.entries(rawMap || {})) {
        const id = extractFileIdFromAppwriteUrl(appwriteUrl);
        if (id) idx[firebaseUrl] = id;
      }
      firebaseToFileId = idx;
      return idx;
    })
    .catch(() => {
      firebaseToFileId = {};
      return firebaseToFileId;
    });

  return mapPromise;
}

// Kick off the map load eagerly so it's ready by the time URLs are rewritten
if (typeof window !== 'undefined') {
  loadFirebaseMap();
}

/** Extract the fileId from any Appwrite-style storage URL. */
function extractFileIdFromAppwriteUrl(url) {
  if (typeof url !== 'string') return null;
  const m = url.match(/\/storage\/buckets\/[^/]+\/files\/([^/]+)\/(?:view|preview|download)/);
  return m ? m[1] : null;
}

/** Build the R2 URL for a given file ID. */
function r2Url(fileId) {
  return `${IMAGE_URL}/files/${fileId}`;
}

/**
 * Rewrite a single URL string. Returns the original string if no rewrite applies.
 * Synchronous fast-path for Appwrite URLs (which contain the fileId directly).
 * Firebase URLs require the lazily-loaded map; if not yet loaded, returns the
 * original (one render frame later it will be ready and the next render will fix it).
 */
export function rewriteImageUrl(url) {
  if (typeof url !== 'string' || url.length === 0) return url;

  // Already an R2 URL — leave alone
  if (url.startsWith(IMAGE_URL)) return url;

  // Appwrite legacy URL (api.villakosh.in or *.cloud.appwrite.io) — extract fileId
  if (url.includes('/storage/buckets/') && url.includes('/files/')) {
    const fileId = extractFileIdFromAppwriteUrl(url);
    if (fileId) return r2Url(fileId);
  }

  // Firebase Storage URL — needs the map
  if (url.includes('firebasestorage.googleapis.com') && firebaseToFileId) {
    const fileId = firebaseToFileId[url];
    if (fileId) return r2Url(fileId);
  }

  return url;
}

/**
 * Recursively walk an object/array and rewrite every string that looks like
 * a known legacy image URL. Returns a new object — does not mutate the input.
 *
 * Also normalizes a few historical inconsistencies:
 *   - `images` stored as a single URL string instead of an array → wrap in array
 *   - empty/null values left as-is
 */
export function rewriteImagesInObject(obj) {
  if (obj == null) return obj;

  if (typeof obj === 'string') return rewriteImageUrl(obj);

  if (Array.isArray(obj)) {
    return obj.map((item) => rewriteImagesInObject(item));
  }

  if (typeof obj === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      // Special case: if a field that should be an array is a single URL string,
      // wrap it into an array so components that .map() over it don't break.
      if ((k === 'images' || k === 'locationImages') && typeof v === 'string' && v.length > 0) {
        out[k] = [rewriteImageUrl(v)];
        continue;
      }
      out[k] = rewriteImagesInObject(v);
    }
    return out;
  }

  return obj;
}
